/**
 * SkillFormDialog Component
 *
 * Dialog for creating and editing catalog skills. Category is the user-facing
 * grouping; the legacy `type` field is derived so older API clients still work.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
  FormHelperText,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useCreateSkill,
  useUpdateSkill,
  type Skill,
} from '@/hooks/useSkills';
import { useNotify } from '@/hooks/useNotify';
import {
  SKILL_CATEGORY_OPTIONS,
  categoryFromLegacyType,
  isSkillCategoryKey,
  legacyTypeForCategory,
  type SkillCategoryKey,
} from '@/utils/skillLevels';

interface SkillFormDialogProps {
  open: boolean;
  skill?: Skill | null;
  onClose: () => void;
}

interface FormData {
  name: string;
  category: SkillCategoryKey;
  description: string;
  sortOrder: number;
  isActive: boolean;
}

const initialFormData: FormData = {
  name: '',
  category: 'service',
  description: '',
  sortOrder: 0,
  isActive: true,
};

function skillCategory(skill: Skill): SkillCategoryKey {
  if (skill.category && isSkillCategoryKey(skill.category)) return skill.category;
  return categoryFromLegacyType(skill.type);
}

export function SkillFormDialog({ open, skill, onClose }: SkillFormDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!skill;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isDirty, setIsDirty] = useState(false);

  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();

  useEffect(() => {
    if (skill) {
      setFormData({
        name: skill.name,
        category: skillCategory(skill),
        description: skill.description ?? '',
        sortOrder: skill.sortOrder,
        isActive: skill.isActive,
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setIsDirty(false);
  }, [skill, open]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('skillForm.nameRequired');
    } else if (formData.name.length > 100) {
      newErrors.name = t('skillForm.nameMaxLength');
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = t('skillForm.descriptionMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const type = legacyTypeForCategory(formData.category);

    try {
      if (isEditing && skill) {
        await updateMutation.mutateAsync({
          id: skill.id,
          data: {
            name: formData.name,
            type,
            category: formData.category,
            description: formData.description || null,
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        notify.success(t('skillForm.updated'), t('skillForm.updatedDesc', { name: formData.name }));
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          type,
          category: formData.category,
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
          ...(formData.description.trim() ? { description: formData.description.trim() } : {}),
        });
        notify.success(t('skillForm.created'), t('skillForm.createdDesc', { name: formData.name }));
      }
      onClose();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('skillForm.saveFailed') }));
    }
  };

  const handleChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('skillForm.editTitle') : t('skillForm.createTitle')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      isLoading={isPending}
      submitLabel={isEditing ? tc('save') : tc('add')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label={t('skills.name')}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
            autoFocus
          />

          <FormControl fullWidth>
            <InputLabel id="skill-form-category">{t('skills.category')}</InputLabel>
            <Select
              labelId="skill-form-category"
              id="skill-form-category-select"
              value={formData.category}
              label={t('skills.category')}
              onChange={(e) => handleChange('category', e.target.value as SkillCategoryKey)}
            >
              {SKILL_CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('skillForm.categoryHint')}
            </FormHelperText>
          </FormControl>

          <TextField
            label={t('skills.description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description ?? t('skillForm.descriptionHint')}
            fullWidth
            multiline
            rows={3}
          />

          <TextField
            label={t('skills.order')}
            type="number"
            value={formData.sortOrder}
            onChange={(e) => handleChange('sortOrder', parseInt(e.target.value, 10) || 0)}
            fullWidth
            helperText={t('skillForm.sortOrderHint')}
            slotProps={{
              htmlInput: { min: 0 },
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
            }
            label={t('active')}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
